import {
  AddComment20,
  ArrowLeft20,
  Chat20,
  CheckmarkOutline20,
  Edit16,
  OverflowMenuVertical20,
  Stamp20,
} from '@carbon/icons-react';
import React, { useCallback, useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import {
  CpBody01Primary,
  CpBody01Secondary,
} from 'components/atoms/customer-portal/typography';
import { Loading } from 'components/atoms/loading';
import { CurrentSpaceContext } from 'components/organisms/spaces/context';
import { RenderRocketlaneDocument } from 'components/organisms/spaces/space-tab/space-tab';
import { useSpaceActions } from 'components/pages/project-details/project-spaces/use-space-actions';
import { useSpaceTabQuery } from 'queries/project-spaces';

const Page = styled.div`
  height: 100%;
  width: 100%;
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
`;

const TitleBar = styled.div`
  width: 100%;
  /* background: black; */
  background: var(--cp-black-opacity-80-hover);
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--cp-spacing-05);
  position: fixed;
  top: 0;
  left: 0;
  padding-left: var(--cp-spacing-06);
  padding-right: var(--cp-spacing-06);
  padding-top: var(--cp-spacing-04);
  padding-bottom: var(--cp-spacing-04);
  --cp-text-primary: var(--cp-white-100);
  --cp-text-secondary: var(--cp-white-100);
  p {
    margin-bottom: 0;
  }
  svg {
    fill: var(--cp-white-100);
  }
`;

const BackWrapper = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  svg {
    margin-right: var(--cp-spacing-03);
  }
`;

const IconWrapper = styled.div`
  padding: var(--cp-spacing-03);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const EditWrapper = styled.div`
  padding-top: var(--cp-spacing-03);
  padding-bottom: var(--cp-spacing-03);
  padding-left: var(--cp-spacing-05);
  padding-right: var(--cp-spacing-05);
  display: none;
  align-items: center;
  justify-content: center;
  svg {
    margin-right: var(--cp-spacing-03);
  }
  border-radius: 2rem;
  cursor: pointer;
  border: 1px solid var(--cp-white-100);

  @media (min-width: 1080px) {
    display: flex;
  }
`;

const ActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--cp-spacing-03);
`;

const Content = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  background: #003147;
  padding-top: var(--cp-spacing-10);
  .ant-tabs,
  [data-cy='spaces-toolbar'],
  .document-tab-container,
  .distraction-free-mode-btn {
    display: none;
  }

  [data-test-id='space.tab'] {
    [class*='document_document--ready'] {
      height: 100%;
    }
  }

  @media (min-width: 1080px) {
    padding-left: max(var(--spacing-10), calc(calc(100% - 1080px) / 2));
    padding-right: max(var(--spacing-10), calc(calc(100% - 1080px) / 2));
  }
`;

export const CpSpaceTabPreview: React.FC<{
  spaceTabId: number;
  spaceId: number;
  projectId: number;
}> = ({ spaceTabId, projectId, spaceId }) => {
  const router = useHistory();
  const { data: spaceTab } = useSpaceTabQuery(spaceTabId);
  const { currentSpaceDetails } = useSpaceActions({
    spaceId: spaceId?.toString() ?? '',
    projectId,
    spaceTabId: spaceTabId?.toString() ?? '',
  });
  const backRef = useRef<boolean | null>(null);
  const docType: 'ROCKETLANE_DOCUMENT' | 'GOOGLE_DOCUMENT' | undefined =
    useMemo(() => {
      return spaceTab?.spaceTabType;
    }, [spaceTab]);

  const onBack = useCallback(() => {
    // go back if history is available
    if (router.length > 1) {
      try {
        router.goBack();
        backRef.current = true;
      } catch (e) {
        if (!backRef.current) {
          onBack();
        }
      }

      return;
    }
    // else go to root
    router.push('/');
  }, [router]);

  if (!spaceTab || !currentSpaceDetails) {
    return (
      <LoadingWrapper>
        <Loading size="large" />
      </LoadingWrapper>
    );
  }

  return (
    <Page>
      <TitleBar>
        <BackWrapper onClick={onBack}>
          <IconWrapper>
            <ArrowLeft20 />
          </IconWrapper>
          <CpBody01Primary>{spaceTab?.spaceTabTitle}</CpBody01Primary>
        </BackWrapper>
        <EditWrapper>
          <Edit16 />
          <CpBody01Secondary>
            <FormattedMessage id="customer_portal.file_preview.edit_the_document" />
          </CpBody01Secondary>
        </EditWrapper>
        <ActionsWrapper>
          <IconWrapper>
            <Chat20 />
          </IconWrapper>
          <IconWrapper>
            <CheckmarkOutline20 />
          </IconWrapper>
          <IconWrapper>
            <AddComment20 />
          </IconWrapper>
          <IconWrapper>
            <Stamp20 />
          </IconWrapper>
          <IconWrapper>
            <OverflowMenuVertical20 />
          </IconWrapper>
        </ActionsWrapper>
      </TitleBar>
      <Content>
        {docType === 'ROCKETLANE_DOCUMENT' ? (
          <CurrentSpaceContext.Provider value={currentSpaceDetails}>
            <RenderRocketlaneDocument
              readonly
              data={{
                ...spaceTab,
                space: spaceTab?.space?.spaceId,
                taskList: spaceTab?.taskList?.map?.((e) => e?.taskId) ?? [],
              }}
            />
          </CurrentSpaceContext.Provider>
        ) : (
          <></>
        )}
      </Content>
    </Page>
  );
};
